import { z } from 'zod';
import type { MentionEntity } from '@terra/shared-dto-chat-types';
import type { MessageHistoryEntry } from './createMessageHistoryController';
import { MENTION_ENTITY_TYPES } from '../utils/mentionEntityType';

export const MAX_PERSISTED_MESSAGE_HISTORY_SESSIONS = 5;

function toNonEmptyTuple<T extends string>(values: readonly T[]): [T, ...T[]] {
  const [first, ...rest] = values;
  if (first === undefined) {
    throw new Error('Expected at least one mention entity type');
  }
  return [first, ...rest];
}

const MentionEntityTypeSchema = z.enum(
  toNonEmptyTuple(
    MENTION_ENTITY_TYPES satisfies readonly MentionEntity['entityType'][],
  ),
);

const MentionEntitySchema = z.object({
  entityType: MentionEntityTypeSchema,
  entityId: z.string(),
  label: z.string(),
  extraData: z.record(z.string(), z.unknown()).optional(),
});

const MessageHistoryMentionSnapshotSchema = z.object({
  id: z.string(),
  label: z.string(),
  type: z.enum(['parent', 'child', 'action', 'resource']).optional(),
  mentionEntity: MentionEntitySchema.optional(),
});

const MentionEntityWithOffsetSchema = z.object({
  offset: z.number(),
  entityId: z.string(),
  entityType: MentionEntityTypeSchema,
  label: z.string(),
});

const MessageHistoryEntrySchema = z.object({
  editorStateJson: z.string(),
  plainText: z.string(),
  mentions: z.array(MessageHistoryMentionSnapshotSchema),
  editorMentions: z.array(MentionEntityWithOffsetSchema),
});

const PersistedMessageHistorySessionSchema = z.object({
  entries: z.array(MessageHistoryEntrySchema),
  lastAccessedAt: z.number(),
});

const PersistedMessageHistoryStoreSchema = z.object({
  sessions: z.record(z.string(), PersistedMessageHistorySessionSchema),
});

type PersistedMessageHistoryStore = z.infer<
  typeof PersistedMessageHistoryStoreSchema
>;

export type MessageHistoryPersistenceStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

const defaultStorage: MessageHistoryPersistenceStorage = {
  getItem: (key) => {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem(key);
  },
  setItem: (key, value) => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(key, value);
  },
};

let resolveStorageKey: () => string | null = () => null;
let persistenceStorage: MessageHistoryPersistenceStorage = defaultStorage;

export function configureMessageHistoryPersistence({
  getStorageKey,
  storage,
}: {
  getStorageKey: () => string | null;
  storage?: MessageHistoryPersistenceStorage;
}): void {
  resolveStorageKey = getStorageKey;
  persistenceStorage = storage ?? defaultStorage;
}

export function resetMessageHistoryPersistenceForTest(): void {
  resolveStorageKey = () => null;
  persistenceStorage = defaultStorage;
}

export function getScopedMessageHistorySessionId({
  sessionId,
}: {
  sessionId: string;
}): string {
  const storageKey = resolveStorageKey() ?? '__unscoped__';
  return `${storageKey}:${sessionId}`;
}

function readStore(): PersistedMessageHistoryStore {
  const storageKey = resolveStorageKey();
  if (!storageKey) {
    return { sessions: {} };
  }

  const raw = persistenceStorage.getItem(storageKey);
  if (!raw) {
    return { sessions: {} };
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    const result = PersistedMessageHistoryStoreSchema.safeParse(parsed);
    if (!result.success) {
      console.warn(
        '[messageHistoryPersistence] Ignoring invalid persisted store',
        {
          error: result.error,
        },
      );
      return { sessions: {} };
    }
    return result.data;
  } catch (error: unknown) {
    console.warn(
      '[messageHistoryPersistence] Failed to parse persisted store',
      {
        error: error instanceof Error ? error : new Error(String(error)),
      },
    );
    return { sessions: {} };
  }
}

function writeStore(store: PersistedMessageHistoryStore): void {
  const storageKey = resolveStorageKey();
  if (!storageKey) {
    return;
  }

  try {
    persistenceStorage.setItem(storageKey, JSON.stringify(store));
  } catch (error: unknown) {
    console.warn(
      '[messageHistoryPersistence] Failed to persist message history',
      {
        error: error instanceof Error ? error : new Error(String(error)),
      },
    );
  }
}

function pruneSessions({
  store,
}: {
  store: PersistedMessageHistoryStore;
}): PersistedMessageHistoryStore {
  const sessionIds = Object.keys(store.sessions);
  if (sessionIds.length <= MAX_PERSISTED_MESSAGE_HISTORY_SESSIONS) {
    return store;
  }

  const sortedSessionIds = sessionIds.sort(
    (left, right) =>
      store.sessions[right].lastAccessedAt -
      store.sessions[left].lastAccessedAt,
  );

  const sessionsToKeep = sortedSessionIds.slice(
    0,
    MAX_PERSISTED_MESSAGE_HISTORY_SESSIONS,
  );

  const prunedSessions: PersistedMessageHistoryStore['sessions'] = {};
  for (const sessionId of sessionsToKeep) {
    prunedSessions[sessionId] = store.sessions[sessionId];
  }

  return { sessions: prunedSessions };
}

export function loadPersistedMessageHistorySession({
  sessionId,
}: {
  sessionId: string;
}): MessageHistoryEntry[] {
  const store = readStore();
  const session = store.sessions[sessionId];
  if (!session) {
    return [];
  }

  return session.entries;
}

export function touchPersistedMessageHistorySession({
  sessionId,
}: {
  sessionId: string;
}): void {
  const store = readStore();
  const existingSession = store.sessions[sessionId];
  if (!existingSession) {
    return;
  }

  store.sessions[sessionId] = {
    ...existingSession,
    lastAccessedAt: Date.now(),
  };

  writeStore(pruneSessions({ store }));
}

export function persistMessageHistorySession({
  sessionId,
  entries,
}: {
  sessionId: string;
  entries: MessageHistoryEntry[];
}): void {
  const store = readStore();
  store.sessions[sessionId] = {
    entries,
    lastAccessedAt: Date.now(),
  };

  writeStore(pruneSessions({ store }));
}
